import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { JSONContent } from '@tiptap/vue-3'
import { useMembersStore } from '../../../stores/members.store'
import { FeedbackTopicType } from '../../../core/models/feedback-topic'
import type { CompleteFeedbackTopic } from '../../../core/composables/use-complete-feedback-topics'
import { SurveyRecurrenceFrequency } from '@/modules/start-survey/composables/use-start-survey'

export const useAddCoacheesWizzard = (
  feedbackTopics: ReadonlyArray<CompleteFeedbackTopic>,
  defaultValue?: Partial<StoreableCoachees>,
) => {
  const { t } = useI18n()
  const memberStore = useMembersStore()

  let numberOfJournal = 0

  const defaultFeedbackTopicTasks: StoreableCoachees['tasks'] = feedbackTopics
    .filter((topic) => topic.defaults && topic.defaults.isActive)
    .map((feedbackTopic) => {
      if (feedbackTopic.type == FeedbackTopicType.Journal) {
        const today = new Date()
        const nextWeek = new Date()
        nextWeek.setDate(today.getDate() + 7 * numberOfJournal)
        numberOfJournal++
        return {
          isActive: true,
          feedbackTopicId: feedbackTopic.id,
          feedbackTopicType: FeedbackTopicType.Journal,
          amount: feedbackTopic.defaults!.amount!,
          frequency: feedbackTopic.defaults!.frequency!,
          startDate: nextWeek,
          weekDay: today.getDay(),
          title: undefined,
          message: undefined,
          isRecurring: false,
          recurrenceFrequency: SurveyRecurrenceFrequency.Week,
          recurrenceInterval: 1,
        }
      } else {
        return {
          isActive: true,
          feedbackTopicId: feedbackTopic.id,
          feedbackTopicType: FeedbackTopicType.Evaluation,
          numberOfSelfFeedbacks: feedbackTopic.defaults!.numberOfSelfFeedbacks ?? 0,
          numberOfPeerFeedbacks: feedbackTopic.defaults!.numberOfPeerFeedbacks ?? 0,
          numberOfExternalFeedbacks: feedbackTopic.defaults!.numberOfExternalFeedbacks ?? 0,
        }
      }
    })

  const formData = reactive<Partial<StoreableCoachees>>(
    defaultValue
      ? defaultValue
      : {
          coacheeIds: [],
          tasks: defaultFeedbackTopicTasks,
        },
  )

  const selectedMemberIds = ref<string[]>(formData.coacheeIds ?? [])

  watch(
    selectedMemberIds,
    () => {
      formData.coacheeIds = [...selectedMemberIds.value]
    },
    { deep: true },
  )

  const removeMember = (memberId: string) => {
    selectedMemberIds.value = selectedMemberIds.value?.filter((id) => id != memberId)
  }

  const addFeedbackTopicTask = (
    task:
      | {
          feedbackTopicId: string
          feedbackTopicType: FeedbackTopicType.Journal | FeedbackTopicType.Superpower
          amount?: number
          frequency?: JournalTaskFrequency
          weekDay?: number
          startDate?: Date
          title?: string
          message?: JSONContent
          isRecurring?: boolean
          recurrenceFrequency?: SurveyRecurrenceFrequency
          recurrenceInterval?: number
        }
      | {
          feedbackTopicId: string
          feedbackTopicType: FeedbackTopicType.Evaluation | FeedbackTopicType.Pulse
          numberOfSelfFeedbacks?: number
          numberOfPeerFeedbacks?: number
          numberOfExternalFeedbacks?: number
        },
  ) => {
    const existingTask = formData.tasks?.find(
      (existingTask) => existingTask.feedbackTopicId == task.feedbackTopicId,
    )

    if (existingTask) {
      existingTask.isActive = true
      return
    }

    const today = new Date()

    if (task.feedbackTopicType == FeedbackTopicType.Journal) {
      formData.tasks?.push({
        isActive: true,
        feedbackTopicId: task.feedbackTopicId,
        feedbackTopicType: FeedbackTopicType.Journal,
        amount: task.amount ?? 1,
        frequency: task.frequency ?? JournalTaskFrequency.Day,
        weekDay: task.weekDay ?? today.getDay(),
        startDate: task.startDate ?? today,
        title: task.title,
        message: task.message,
        isRecurring: task.isRecurring ?? false,
        recurrenceFrequency: task.recurrenceFrequency ?? SurveyRecurrenceFrequency.Week,
        recurrenceInterval: task.recurrenceInterval ?? 1,
      })
    }

    if (task.feedbackTopicType == FeedbackTopicType.Evaluation) {
      formData.tasks?.push({
        isActive: true,
        feedbackTopicId: task.feedbackTopicId,
        feedbackTopicType: FeedbackTopicType.Evaluation,
        numberOfPeerFeedbacks:
          task.numberOfPeerFeedbacks == undefined ? 0 : task.numberOfPeerFeedbacks,
        numberOfSelfFeedbacks:
          task.numberOfSelfFeedbacks == undefined ? 0 : task.numberOfSelfFeedbacks,
        numberOfExternalFeedbacks:
          task.numberOfExternalFeedbacks == undefined ? 0 : task.numberOfExternalFeedbacks,
      })
    }
  }

  const removeFeedbackTopicTask = (feedbackTopicId: string) => {
    formData.tasks!.find((task) => task.feedbackTopicId == feedbackTopicId)!.isActive = false
  }

  const toggleFeedbackTopicTask = (
    feedbackTopicId: string,
    feedbackTopicType: FeedbackTopicType,
  ) => {
    if (formData.tasks?.find((task) => task.feedbackTopicId == feedbackTopicId && task.isActive)) {
      removeFeedbackTopicTask(feedbackTopicId)
    } else {
      addFeedbackTopicTask({
        feedbackTopicId: feedbackTopicId,
        feedbackTopicType: feedbackTopicType,
      })
    }
  }

  const errors = ref<ErrorMap>(new Map())

  const clearForm = () => {
    formData.coacheeIds = []
    errors.value.clear()
  }

  watch(
    () => formData.coacheeIds?.length,
    () => {
      errors.value.delete('members')
    },
  )
  watch(
    () => formData.tasks,
    () => {
      for (const topic of feedbackTopics) {
        errors.value.delete(topic.id)
      }
    },
    { deep: true },
  )

  const selectedMembers = computed(() => {
    return memberStore.getMembersByIds(formData.coacheeIds ?? [])
  })

  const currentStepIndex = ref(0)

  const currentStep = computed(() => {
    return steps.value[currentStepIndex.value]
  })

  const isLastStep = computed(() => {
    return currentStepIndex.value == steps.value.length - 1
  })

  const isFirstStep = computed(() => {
    return currentStepIndex.value == 0
  })

  const currentFeedbackTopic = computed(() => {
    return feedbackTopics.find((topic) => topic.id == currentStep.value)
  })

  const steps = computed(() => {
    const steps = ['setup']

    for (const feedbackTopic of feedbackTopics) {
      steps.push(feedbackTopic.id)
    }

    // steps.push('notification')
    return steps
  })

  const nextStep = () => {
    validateStep()

    if (errors.value.size > 0) return

    if (currentStepIndex.value < steps.value.length) {
      currentStepIndex.value++
    }
  }

  const previousStep = () => {
    if (currentStepIndex.value == 0) return

    currentStepIndex.value--
  }

  const validateStep = () => {
    if (currentStep.value == 'setup') {
      if (!formData.coacheeIds || formData.coacheeIds.length == 0) {
        errors.value.set('members', t('teams.errors.members'))
      }
      return
    }

    const feedbackTopic = feedbackTopics.find((topic) => topic.id == currentStep.value)
    if (!feedbackTopic) {
      return
    }

    if (feedbackTopic.type == FeedbackTopicType.Evaluation) {
      const task = formData.tasks?.find(
        (currentTask) =>
          currentTask.feedbackTopicId == feedbackTopic.id &&
          currentTask.feedbackTopicType == FeedbackTopicType.Evaluation &&
          currentTask.isActive,
      ) as EvaluationTask | undefined

      if (
        task &&
        task.numberOfPeerFeedbacks == 0 &&
        task.numberOfSelfFeedbacks == 0 &&
        task.numberOfExternalFeedbacks == 0
      ) {
        errors.value.set(feedbackTopic.id, 'Wähle mindestens einen Zielwert, der grösser als 0 ist')
      }
      return
    }

    if (feedbackTopic.type == FeedbackTopicType.Journal) {
      const task = formData.tasks?.find(
        (currentTask) =>
          currentTask.feedbackTopicId == feedbackTopic.id &&
          currentTask.feedbackTopicType == FeedbackTopicType.Journal &&
          currentTask.isActive,
      ) as JournalTask | undefined

      if (!task) {
        return
      }

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const startDate = new Date(task.startDate)
      startDate.setHours(0, 0, 0, 0)

      if (startDate < today) {
        errors.value.set(feedbackTopic.id, 'Das Startdatum darf nicht in der Vergangenheit liegen')
        return
      }

      if (task.isRecurring) {
        const interval = Number(task.recurrenceInterval)
        if (!Number.isInteger(interval) || interval < 1) {
          errors.value.set(feedbackTopic.id, t('feedback.formValidation.recurrenceInterval'))
          return
        }

        if (task.recurrenceFrequency === SurveyRecurrenceFrequency.Week && ![1, 2, 4].includes(interval)) {
          errors.value.set(feedbackTopic.id, t('feedback.formValidation.recurrenceWeeklyInterval'))
        }
      }
    }
  }

  return {
    formData,
    selectedMemberIds,
    errors,
    removeMember,
    clearForm,
    selectedMembers,
    toggleFeedbackTopicTask,
    nextStep,
    previousStep,
    currentStep,
    currentFeedbackTopic,
    isLastStep,
    isFirstStep,
    steps,
    currentStepIndex,
  }
}

export interface StoreableCoachees {
  coacheeIds: string[]
  tasks: FeedbackTopicTask[]
}

type ErrorMap = Map<string, string>

export type FeedbackTopicTask = JournalTask | EvaluationTask

export interface JournalTask {
  isActive: boolean
  feedbackTopicId: string
  feedbackTopicType: FeedbackTopicType.Journal
  amount: number
  frequency: JournalTaskFrequency
  weekDay?: number
  startDate: Date
  title?: string
  message?: JSONContent
  isRecurring: boolean
  recurrenceFrequency?: SurveyRecurrenceFrequency
  recurrenceInterval?: number
}
export interface EvaluationTask {
  isActive: boolean
  feedbackTopicId: string
  feedbackTopicType: FeedbackTopicType.Evaluation
  numberOfSelfFeedbacks: number
  numberOfPeerFeedbacks: number
  numberOfExternalFeedbacks: number
}

export enum JournalTaskFrequency {
  Day = 'day',
  Week = 'week',
  Month = 'month',
}
